import { Button, ButtonGroup } from "@components/button";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { isValidDate } from "@components/inputs/date/utils";
import { ActionType, addEmptyLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import SmartField, { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { validateDelete } from "@components/smart/smartFormDeleteButton/SmartDelete.utils";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import BindingContext from "@odata/BindingContext";
import { getNewItemsMaxId } from "@odata/Data.utils";
import {
    EntitySetName,
    IPrEmployeeEntity,
    IPrTaxDiscountTypeEntity,
    IPrTaxpayerDeclarationChildEntity,
    IPrTaxpayerDeclarationDiscountEntity,
    IPrTaxpayerDeclarationEntity,
    PrEmployeeDependentPersonEntity,
    PrEmployeeEntity,
    PrEmployeeTemporalEntity,
    PrOtherProviderAddressEntity,
    PrOtherProviderEmployerAddressEntity,
    PrTaxpayerDeclarationChildEntity,
    PrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationOtherProviderEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrChildDeductionOrderCode,
    PrDisabilityTypeCode,
    PrEmployeeDependentPersonRelationCode,
    PrTaxDiscountTypeCode,
    PrTaxpayerDeclarationStatusCode
} from "@odata/GeneratedEnums";
import { transformToODataString } from "@odata/OData.utils";
import { setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import { getEndOfLastProcessedPeriod, getOldestUnprocessedPeriodDate } from "@pages/payroll/payroll.utils";
import { TempDateValidToPath } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.def";
import {
    commonTaxpayerDeclarationOnChange,
    disabilityDiscountType,
    getTogglePropPath,
    hasDisabilityDiscount,
    loadPrTaxDiscountTypes,
    massExportToPDF,
    taxpayerDeclarationOnAfterLoad,
    taxpayerDeclarationOnBeforeSave
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import { ICopyEntityArgs } from "@utils/DraftUtils";
import { isDefined } from "@utils/general";
import { logger } from "@utils/log";
import { memoizeOne } from "@utils/memoizeOne";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { ValueType } from "../../../enums";
import { DATE_MIN, getUtcDayjs } from "../../../types/Date";
import { IContextInitArgs, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import { isMassExportAction } from "../../../views/table/TableToolbar.utils";

export interface ITaxPayerDeclarationCustomData extends IFormStorageDefaultCustomData {
    discountTypes?: Record<string, IPrTaxDiscountTypeEntity>;
    isRenderedDateAppliedToDialog?: boolean;
}

interface IProps extends IFormViewProps<IPrTaxpayerDeclarationEntity, ITaxPayerDeclarationCustomData>, WithConfirmationDialog {
}

class TaxpayerDeclarationFormView extends FormViewForExtend<IPrTaxpayerDeclarationEntity, IProps> {

    getAdditionalLoadPromise(args: IContextInitArgs) {
        return [
            loadPrTaxDiscountTypes(this.props.storage),
            ...super.getAdditionalLoadPromise(args)
        ];
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        const { storage } = this.props;
        if (isMassExportAction(actionId)) {
            await massExportToPDF([this.props.storage.data.bindingContext.getKey()?.toString()], storage.context);
        }

        return super.handleCustomHeaderAction(actionId);
    }

    setDefaultDateApplyFrom = async (): Promise<void> => {
        const storage = this.props.storage;
        const lastProcessedWageDate = await getOldestUnprocessedPeriodDate(storage);
        if (lastProcessedWageDate !== DATE_MIN && isValidDate(lastProcessedWageDate)) {
            storage.setValueByPath(PrTaxpayerDeclarationEntity.DateApplyFrom, getUtcDayjs(lastProcessedWageDate).toDate());
        }
    };

    async onAfterLoad() {
        const storage = this.props.storage;

        if (storage.data.bindingContext.isNew()) {
            await this.setDefaultDateApplyFrom();
            if (this.props.formProps?.defaultData?.Employee) {
                this.entity.Employee = this.props.formProps.defaultData.Employee;
                await this.fillDataFromLastEmployeeDeclaration(this.entity.Employee.Id);
            }
            await setMatchingNumberRange(storage, true);
            this.entity.Discounts = [
                this.getNewDiscountItem(PrTaxDiscountTypeCode.TaxPayerDiscount)
            ];
        } else {
            this.canBeDeleted = await this.canBeDeletedMemoized();
        }

        storage.data.disabled = this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.WaitingForSignature;

        taxpayerDeclarationOnAfterLoad(this.entity, storage);

        return super.onAfterLoad();
    }

    getNewDiscountItem = (typeCode: PrTaxDiscountTypeCode) => {
        const storage = this.props.storage;
        const newItemsMax = getNewItemsMaxId(this.entity.Discounts ?? []);

        const newItem = addEmptyLineItem<IPrTaxpayerDeclarationDiscountEntity>({
            storage,
            bindingContext: storage.data.bindingContext.navigate(PrTaxpayerDeclarationEntity.Discounts),
            newItemsCount: newItemsMax + 1,
            context: storage.context,
            columns: []
        });

        newItem.DiscountTypeCode = typeCode;
        return newItem;
    };

    fillDataFromLastEmployeeDeclaration = async (employeeId: number): Promise<void> => {
        const storage = this.props.storage;
        let dataToCopy: Partial<IPrTaxpayerDeclarationEntity>;
        try {
            const res = await storage.oData.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations)
                .query()
                .expand(PrTaxpayerDeclarationEntity.OtherProvider, query => {
                    return query.expand(PrTaxpayerDeclarationOtherProviderEntity.Address, q2 => q2.expand(PrOtherProviderAddressEntity.Country))
                        .expand(PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, q2 => q2.expand(PrOtherProviderEmployerAddressEntity.Country));
                })
                .expand(PrTaxpayerDeclarationEntity.Discounts)
                .expand(PrTaxpayerDeclarationEntity.Children, query => query.expand(PrTaxpayerDeclarationChildEntity.Order))
                .filter(`Employee/Id eq ${employeeId} AND StatusCode eq '${PrTaxpayerDeclarationStatusCode.Signed}'`)
                .orderBy("Year", false)
                .top(1)
                .fetchData<IPrTaxpayerDeclarationEntity[]>();

            if (res.value?.length) {
                const copy = this.createEntityCopy({ entity: res.value[0] });
                dataToCopy = {
                    Discounts: copy.Discounts,
                    Children: copy.Children,
                    OtherProvider: copy.OtherProvider
                };
            } else {
                // initialize Children from Employee Dependents of type Child
                const q = storage.oData.getEntitySetWrapper(EntitySetName.PrEmployees)
                    .query(employeeId)
                    .expand(PrEmployeeEntity.TemporalPropertyBag, q2 => q2.select(PrEmployeeTemporalEntity.PrDisabilityTypeCode, PrEmployeeTemporalEntity.DateValidFrom,
                        PrEmployeeTemporalEntity.DateValidTo, PrEmployeeTemporalEntity.IsZtpP))
                    .expand(PrEmployeeEntity.Dependents, q2 =>
                        q2.filter(`${PrEmployeeDependentPersonEntity.RelationCode} eq ${transformToODataString(PrEmployeeDependentPersonRelationCode.Child, ValueType.String)}`)
                            .select(PrEmployeeDependentPersonEntity.Id, PrEmployeeDependentPersonEntity.FirstName,
                                PrEmployeeDependentPersonEntity.LastName, PrEmployeeDependentPersonEntity.PersonalIdentificationNumber));

                const employeeRes = await q.fetchData<IPrEmployeeEntity>();
                if (employeeRes?.value) {
                    const employee = employeeRes.value;
                    const preferredOrder = [PrChildDeductionOrderCode._1stChild, PrChildDeductionOrderCode._2ndChild, PrChildDeductionOrderCode._3rdChild];

                    const thisYearDayJs = getUtcDayjs(this.entity.DateApplyFrom);
                    const matchingTemporalBag = employee.TemporalPropertyBag.find(tb => {
                        return thisYearDayJs.isBetween(getUtcDayjs(tb.DateValidFrom), getUtcDayjs(tb.DateValidTo), null, "[]");
                    });

                    if (matchingTemporalBag?.PrDisabilityTypeCode) {
                        if (matchingTemporalBag.IsZtpP) {
                            storage.setValueByPath(getTogglePropPath(PrTaxDiscountTypeCode.DiscountForZTP_PHolder), matchingTemporalBag.IsZtpP);
                        }
                        if (isDefined(matchingTemporalBag.PrDisabilityTypeCode)) {
                            storage.setValueByPath(hasDisabilityDiscount, true);
                            switch (matchingTemporalBag.PrDisabilityTypeCode) {
                                case PrDisabilityTypeCode.I_Disability:
                                case PrDisabilityTypeCode.II_Disability:
                                    storage.setValueByPath(disabilityDiscountType, PrTaxDiscountTypeCode.BasicDisabilityDiscount);
                                    break;
                                case PrDisabilityTypeCode.III_Disability:
                                case PrDisabilityTypeCode.III_DisabilityAbilityToWorkOnlyUnderExceptionalConditions:
                                    storage.setValueByPath(disabilityDiscountType, PrTaxDiscountTypeCode.ExtendedDisabilityDiscount);
                                    break;
                            }
                        }
                    } else {
                        storage.setValueByPath(hasDisabilityDiscount, false);
                        storage.clearValueByPath(disabilityDiscountType);
                        storage.setValueByPath(getTogglePropPath(PrTaxDiscountTypeCode.DiscountForZTP_PHolder), false);
                    }

                    dataToCopy = {
                        Children: employee.Dependents.map((d, idx) => {
                            const val: IPrTaxpayerDeclarationChildEntity = BindingContext.createNewEntity(idx, {
                                [PrTaxpayerDeclarationChildEntity.FirstName]: d.FirstName,
                                [PrTaxpayerDeclarationChildEntity.LastName]: d.LastName,
                                [PrTaxpayerDeclarationChildEntity.PersonalIdentificationNumber]: d.PersonalIdentificationNumber,
                                [PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount]: false,
                                [PrTaxpayerDeclarationChildEntity.OrderCode]: preferredOrder.shift() ?? PrChildDeductionOrderCode.NotApplied,
                            });
                            val.Order = { Code: val.OrderCode };
                            return val;
                        })
                    };
                }
            }
        } catch (e) {
            logger.error("Couldn't fetch data for TaxpayerDeclaration defaulting", e);
        }

        if (dataToCopy) {
            storage.data.entity = {
                ...this.entity,
                ...dataToCopy
            };
            taxpayerDeclarationOnAfterLoad(storage.data.entity, storage);
            storage.refresh();
        }
    };

    handleEmployeeChange = async (e: ISmartFieldChange): Promise<void> => {
        const storage = this.props.storage;
        const path = e.bindingContext.getPath();
        // possibly not needed condition - employee should not be changed if declaration is not new
        const isNew = storage.data.bindingContext.isNew();

        // if employee is changed and declaration is new, or if DateApplyFrom is changed and employee is selected -
        // in both cases we want to try to default data based on last declaration of the employee, as some default data are temporal so date matters
        const dateAppliedFromChange = path === PrTaxpayerDeclarationEntity.DateApplyFrom && isDefined(this.entity.Employee?.Id);
        const employeeId = dateAppliedFromChange ? this.entity.Employee?.Id : e.value;

        if (isNew && ((path === PrTaxpayerDeclarationEntity.Employee && e.triggerAdditionalTasks) || dateAppliedFromChange)) {
            await this.fillDataFromLastEmployeeDeclaration(Number(employeeId));
        }
    };

    handleChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage;
        storage.handleChange(e);

        this.handleEmployeeChange(e);
        commonTaxpayerDeclarationOnChange(e, storage);
    };

    handleSignClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Signed;
        this.save({ data });
    };

    handleSendToSignClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.WaitingForSignature;
        this.save({ data });
    };

    handleSaveDraftClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Draft;
        this.save({ data });
    };

    handleInvalidateClick = async (): Promise<void> => {
        const { confirmationDialog, storage } = this.props;
        storage.setDefaultValueByPath(TempDateValidToPath);
        const enOfLastWageDate = await getEndOfLastProcessedPeriod(storage);
        const isConfirmed = await confirmationDialog.open({
            dialogProps: {
                title: storage.t("TaxpayerDeclaration:Form.InvalidateDeclaration")
            },
            content: <SmartField storage={storage}
                                 fieldProps={{
                                     isReadOnly: false,
                                 }}
                                 fieldDef={{
                                     fieldSettings: {
                                         minDate: enOfLastWageDate,
                                     }
                                 }}
                                 onChange={this.handleChange}
                                 bindingContext={storage.data.bindingContext.navigate(TempDateValidToPath)}/>
        });
        if (isConfirmed) {
            const data = this.onBeforeSave();
            data.DateApplyTo = storage.getValueByPath(TempDateValidToPath);
            data.StatusCode = PrTaxpayerDeclarationStatusCode.Ended;
            this.save({ data });
        }
    };

    handleEditClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Draft;
        this.save({ data });
    };

    createEntityCopy(args: ICopyEntityArgs = {}): IPrTaxpayerDeclarationEntity {
        const copy = cloneDeep(args.entity ?? this.entity);
        const deleteProps = [PrTaxpayerDeclarationEntity.Id, PrTaxpayerDeclarationEntity.StatusCode, PrTaxpayerDeclarationEntity.Status,
            PrTaxpayerDeclarationEntity.DateSigned, PrTaxpayerDeclarationEntity.DateEnded, PrTaxpayerDeclarationEntity.NumberOurs];
        for (const prop of deleteProps) {
            delete copy[prop];
        }
        return copy;
    }

    canBeDeleted: boolean = null;
    canBeDeletedMemoized = memoizeOne(async (): Promise<boolean> => {
        const errors = await validateDelete({
            bindingContext: this.props.storage.data.bindingContext,
            ids: [this.props.storage.data.bindingContext.getKey()]
        });
        return !errors?.length || errors[0]?.ErrorCodes?.length === 0;
    }, () => [this.entity.StatusCode]);

    renderButtons = (): ReactElement => {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;

        const isDeletable = this.isDeletable && this.canBeDeleted === true;
        if (this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.Signed) {
            return <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                {isDeletable &&
                    <Button hotspotId={"formDelete"}
                            onClick={this.handleDelete}
                            isDisabled={storage.isDisabled}
                            isTransparent>{this.props.storage.t("Common:General.Delete")}</Button>}
                {!isDeletable && <Button hotspotId={"formSave"}
                                         onClick={this.handleInvalidateClick}>
                    {storage.t("TaxpayerDeclaration:Form.Invalidate")}
                </Button>}
            </ButtonGroup>;
        }


        if (this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.Ended) {
            return <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                <Button hotspotId={"formCopy"}
                        onClick={this.handleCopy}
                        isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>
            </ButtonGroup>;
        }

        return (
            <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                {this.isDeletable &&
                    <SmartFormDeleteButton storage={storage}
                                           onClick={this.handleDelete}/>
                }
                {this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.WaitingForSignature &&
                    <Button hotspotId={"edit"}
                            onClick={this.handleEditClick}
                            isTransparent>
                        {storage.t("Common:General.Edit")}
                    </Button>}

                {!isFormReadOnly && <>
                    {this.entity.StatusCode !== PrTaxpayerDeclarationStatusCode.WaitingForSignature &&
                        <Button hotspotId={"saveDraft"}
                                onClick={this.handleSaveDraftClick}
                                isTransparent
                                isDisabled={this.shouldDisableSaveButton()}>
                            {storage.t("TaxpayerDeclaration:Form.Save")}
                        </Button>}
                    <Button onClick={this.handleSignClick}
                        /*isTransparent*/>
                        {storage.t("TaxpayerDeclaration:Form.Sign")}
                    </Button>
                    {/*<Button hotspotId={"formSave"} // temporarily removed - https://solitea-cz.atlassian.net/browse/DEV-43124 */}
                    {/*        onClick={this.handleSendToSignClick}*/}
                    {/*        isDisabled={this.shouldDisableSaveButton() || this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.WaitingForSignature}>*/}
                    {/*    {storage.t("TaxpayerDeclaration:Form.SendToSign")}*/}
                    {/*</Button>*/}
                </>}
            </ButtonGroup>
        );
    };

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent): void => {
        this.props.storage.handleLineItemsAction(args);

        if (args.actionType === ActionType.Add) {
            const [item] = args.affectedItems;
            const newChild = (item as IPrTaxpayerDeclarationChildEntity);
            newChild.IsDisabilityDiscount = false;
            const childrenCount = this.entity.Children?.length ?? 0;
            newChild.OrderCode = childrenCount === 0 ? PrChildDeductionOrderCode._1stChild : childrenCount > 2 ? PrChildDeductionOrderCode._3rdChild : PrChildDeductionOrderCode._2ndChild;
            newChild.Order = {
                Code: newChild.OrderCode as PrChildDeductionOrderCode
            };
        }

        this.props.storage.refresh();
    };

    onBeforeSave(): IPrTaxpayerDeclarationEntity {
        const storage = this.props.storage;
        const entity = cloneDeep(this.entity);
        storage.clearEmptyLineItems(PrTaxpayerDeclarationEntity.Children, true);
        return taxpayerDeclarationOnBeforeSave(entity, storage);
    }
}

export default withPermissionContext(withConfirmationDialog(TaxpayerDeclarationFormView));